import { useState, useEffect } from 'react';
import moment from 'moment';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useMediaQuery } from 'react-responsive';
import CreateCharts from '@IssueDashboards/CreateCharts';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import StatusCharts from '@IssueDashboards/StatusCharts';
import AnalyticsCharts from '@IssueDashboards/AnalyticsCharts';
import UserTicketBracketCharts from '@IssueDashboards/UserTicketBracketCharts';
import FormUserList from '@IssueConsoleFunctionalDisplay/FormUserList';
import FormStats from '@IssueDashboards/FormStats';
import TicketForm from '@IssueConsoleForms/TicketForm';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import FormAgentService from '@Helpers/URLHelpers/KaiziqResolve/FormAgentService';
import KaiziqResolveConnectionExpandService from '@Helpers/URLHelpers/KaiziqResolve/views/KaiziqResolveConnectionExpandService';
import KaiziqResolveConnectionAnalyticsService from '@Helpers/URLHelpers/KaiziqResolve/views/KaiziqResolveConnectionAnalyticsService';
import TicketUserEmailCountBracketService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountBracketService';
import TicketUserEmailCountViewService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountViewService';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './FormOverviewPage.scss';

export default function FormOverviewPage(props) {
    const [pageLoader, setPageLoader] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [kaiziqResolveConnectionStats, setKaiziqResolveConnectionStats] = useState({});
    const [kaiziqResolveConnectionAnalytics, setKaiziqResolveConnectionAnalytics] = useState({});
    const [ticketUserEmailCountBracket, setTicketUserEmailCountBracket] = useState([]);
    const [project, setProject] = useState({});
    const [, setFormAgentList] = useState([]);
    const [ticketStatusList, setTicketStatusList] = useState([]);
    const [, setInternetException] = useState(false);
    const [dashboardSubset, setDashboardSubset] = useState({});
    const [topReporter, setTopReporter] = useState({});
    const [, setDetails] = useState({});
    const [, setFormSettings] = useState({});
    const [formType, setFormType] = useState('ticket');
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const is0To768 = useMediaQuery({ maxWidth: 768 });
    const is0To992 = useMediaQuery({ maxWidth: 992 });

    const dashboardSet = {
        analyticsCharts: { visibility: true, displayName: 'Analytics Overview', xs: 12, id: 1 },
        statusCharts: { visibility: true, displayName: 'Status Distribution', xs: (is0To992 ? 12 : 4), id: 2 },
        createCharts: { visibility: true, displayName: 'Ticket Timeline', xs: (is0To992 ? 12 : 8), id: 3 },
        formUserList: { visibility: true, displayName: 'Top Contributors', xs: (is0To992 ? 12 : 8), id: 4 },
        userTicketBracketCharts: { visibility: true, displayName: 'User Activity', xs: (is0To992 ? 12 : 4), id: 5 }
    };

    const { currentProject, currentUserProjectPermissions } = useReduxProject();
    const kaiziqResolveConnectionExpandService = new KaiziqResolveConnectionExpandService();
    const kaiziqResolveConnectionAnalyticsService = new KaiziqResolveConnectionAnalyticsService();
    const ticketUserEmailCountBracketService = new TicketUserEmailCountBracketService();
    const ticketUserEmailCountViewService = new TicketUserEmailCountViewService();
    const errorHandler = new ErrorHandler();
    const formAgentService = new FormAgentService();
    const ticketService = new TicketService();

    let interval;
    const className = 'resolve__formOverviewPage';

    useEffect(() => {
        setPageLoader(true);
        setKaiziqResolveConnection(props.kaiziqResolveConnection);
        renderData();
        interval = setInterval(() => {
            renderData(true);
        }, 30000);
        return () => clearInterval(interval);
    }, [is0To768, is0To992]);

    const renderData = async (isAutoRefresh = false) => {
        try {
            if (!isAutoRefresh) setPageLoader(true);
            else setRefreshing(true);

            await renderFormAgentList();
            setProject(currentProject);
            let detailsObj = JSON.parse(props.kaiziqResolveConnection.details);
            setDetails(detailsObj);
            setFormSettings(detailsObj?.settings);
            setFormType(detailsObj?.settings?.formType);

            let statsObj = await kaiziqResolveConnectionExpandService.getById(props.kaiziqResolveConnection.id);
            statsObj = JSON.parse(statsObj.body);
            setKaiziqResolveConnectionStats(statsObj);

            let analyticsObj = await kaiziqResolveConnectionAnalyticsService.getByFormId(props.kaiziqResolveConnection?.id);
            analyticsObj = JSON.parse(analyticsObj.body);
            setKaiziqResolveConnectionAnalytics(analyticsObj);

            let bracketObj = await ticketUserEmailCountBracketService.getListByForm(props.kaiziqResolveConnection?.id);
            bracketObj = JSON.parse(bracketObj.body);
            setTicketUserEmailCountBracket(bracketObj);

            let statusListObj = await ticketService.getAnalysisByForm(props.kaiziqResolveConnection?.id);
            statusListObj = JSON.parse(statusListObj.body);
            setTicketStatusList(statusListObj.statusList);

            let topObj = await ticketUserEmailCountViewService.getTopByForm(props.kaiziqResolveConnection?.id);
            topObj = JSON.parse(topObj.body);
            setTopReporter(topObj);

            setDashboardSubset(dashboardSet);
            setLastUpdated(new Date());
        } catch (err) {
            errorHandler.handleError(err);
        }
        setPageLoader(false);
        setRefreshing(false);
    };

    const renderFormAgentList = async () => {
        let loadLoops = 0, loadingError = false, errorException;
        for (loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let listObj = await formAgentService.getListByForm(props.kaiziqResolveConnection.id);
                listObj = JSON.parse(listObj.body);
                setFormAgentList(listObj);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Form Agent List Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if (loadLoops === 10 && loadingError) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return false;
        }
        return true;
    };

    const handleManualRefresh = () => renderData(false);

    const populateDashboards = () => {
        let dashboardList = [];
        let dashboardTypes = Object.keys(dashboardSubset)
            .sort((a, b) => dashboardSubset[a].id - dashboardSubset[b].id);

        for (let i = 0; i < dashboardTypes.length; i++) {
            const key = dashboardTypes[i];
            if (!dashboardSet[key].visibility) continue;
            const DashboardType = types[key];
            dashboardList.push(
                <Grid item xs={12} md={dashboardSubset[key].xs} key={key}>
                    <Paper className={className + '__dashboard'} elevation={0}>
                        <Box className={className + '__dashboard__content'}>
                            <DashboardType
                                kaiziqResolveConnection={kaiziqResolveConnection}
                                kaiziqResolveConnectionStats={kaiziqResolveConnectionStats}
                                ticketStatusList={ticketStatusList}
                                ticketUserEmailCountBracket={ticketUserEmailCountBracket}
                                kaiziqResolveConnectionAnalytics={kaiziqResolveConnectionAnalytics}
                                topReporter={topReporter}
                                project={project}
                                formType={formType}
                                reloadData={renderData}
                                handleFormTabPositionClick={props.handleFormTabPositionClick}
                                applyFilterForTicketsPage={props.applyFilterForTicketsPage}
                                createTimeStampList={props.createTimeStampList}
                            />
                        </Box>
                    </Paper>
                </Grid>
            );
        }
        return <Grid container spacing={2}>{dashboardList}</Grid>;
    };

    const renderLoader = () => (
        <Box className={className + '__loader'}>
            <Grid container spacing={2}>
                {[...Array(4)].map((_, i) => (
                    <Grid item xs={6} sm={3} key={i}>
                        <Skeleton variant="rectangular" height={120} style={{ borderRadius: '16px' }} />
                    </Grid>
                ))}
            </Grid>
            <Box mt={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Skeleton variant="rectangular" height={350} style={{ borderRadius: '16px' }} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Skeleton variant="rectangular" height={350} style={{ borderRadius: '16px' }} />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );

    return (
        <div className={className}>
            <Box className={className + '__container'}>
                {/* Header Section */}
                <Box className={className + '__header'}>
                    <Box className={className + '__header__left'}>
                        <Typography variant="h5" className={className + '__header__title'}>
                            Form Overview
                        </Typography>
                        <Box className={className + '__header__meta'}>
                            <Chip
                                label={`Last updated: ${moment(lastUpdated).fromNow()}`}
                                size="small"
                                className={className + '__header__updateChip'}
                            />
                            {refreshing && <LinearProgress className={className + '__header__progress'} />}
                        </Box>
                    </Box>
                    <Box className={className + '__header__actions'}>
                        <Tooltip title="Refresh data">
                            <IconButton onClick={handleManualRefresh} disabled={refreshing} size="small">
                                <RefreshIcon className={refreshing ? 'rotating' : ''} />
                            </IconButton>
                        </Tooltip>
                        {!pageLoader && (currentUserProjectPermissions.allowAdmin || props.kaiziqResolveConnection.connectionLive) && (
                            <TicketForm kaiziqResolveConnection={props.kaiziqResolveConnection} />
                        )}
                    </Box>
                </Box>

                {/* Description Section */}
                {kaiziqResolveConnection?.description &&
                 kaiziqResolveConnection?.description?.trim() !== '<p></p>' && (
                    <Paper className={className + '__description'} elevation={0}>
                        <div dangerouslySetInnerHTML={{ __html: kaiziqResolveConnection?.description || '' }} />
                    </Paper>
                )}

                {/* Main Content */}
                {pageLoader ? renderLoader() : (
                    <Box className={className + '__content'}>
                        <Box className={className + '__dashboards'}>
                            {populateDashboards()}
                        </Box>
                    </Box>
                )}
            </Box>
        </div>
    );
}

const types = {
    analyticsCharts: AnalyticsCharts,
    statusCharts: StatusCharts,
    formStats: FormStats,
    createCharts: CreateCharts,
    userTicketBracketCharts: UserTicketBracketCharts,
    formUserList: FormUserList
};
